/**
 * Format amount in Indian number system: ₹1,23,456.78
 */
export function formatINR(amount: number | string | null | undefined, decimals: boolean = true): string {
  if (amount == null) return decimals ? '₹0.00' : '₹0';
  let num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (!isFinite(num) || isNaN(num)) return decimals ? '₹0.00' : '₹0';
  // Guard against string-concatenation corruption (e.g. "450000045000004500000")
  // that would otherwise render as 4.5e+107. 1 quadrillion INR is a safe ceiling
  // for any legitimate business number.
  if (Math.abs(num) > 1e15) num = 0;

  const isNegative = num < 0;
  const absNum = Math.abs(num);
  const rounded = decimals ? absNum.toFixed(2) : Math.round(absNum).toString();
  const [intPart, decPart] = rounded.split('.');

  let formatted = '';
  const digits = intPart!;

  if (digits.length <= 3) {
    formatted = digits;
  } else {
    const lastThree = digits.slice(-3);
    const remaining = digits.slice(0, -3);
    const pairs: string[] = [];
    for (let i = remaining.length; i > 0; i -= 2) {
      pairs.unshift(remaining.slice(Math.max(0, i - 2), i));
    }
    formatted = pairs.join(',') + ',' + lastThree;
  }

  return `${isNegative ? '-' : ''}₹${formatted}${decimals && decPart ? '.' + decPart : ''}`;
}

/**
 * Parse an INR-formatted string (with commas, optional ₹) to a raw number.
 * Returns 0 for invalid/empty input.
 */
export function parseINR(value: string | number | null | undefined): number {
  if (value == null) return 0;
  if (typeof value === 'number') return isFinite(value) ? value : 0;
  const cleaned = value.replace(/[₹,\s]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return 0;
  const num = parseFloat(cleaned);
  return isFinite(num) ? num : 0;
}

/**
 * Format a number for display inside an input field (Indian comma system, no ₹ symbol).
 * Returns empty string for 0/null so placeholder can show.
 * Preserves trailing decimal point and trailing zeros while typing.
 */
export function formatINRInput(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';

  const str = typeof value === 'number' ? String(value) : value;
  const cleaned = str.replace(/[₹,\s]/g, '');

  if (cleaned === '' || cleaned === '-') return cleaned;

  // Preserve typing state: if user is typing a decimal, keep it as-is temporarily
  const hasTrailingDot = cleaned.endsWith('.');
  const decimalMatch = cleaned.match(/\.(\d*)$/);

  const num = parseFloat(cleaned);
  if (!isFinite(num) || isNaN(num)) return '';
  if (num === 0 && !hasTrailingDot && !(decimalMatch && decimalMatch[1]!.length > 0)) return '';

  const isNegative = num < 0;
  const absStr = cleaned.replace(/^-/, '');

  // Split on decimal
  const parts = absStr.split('.');
  const intPart = parts[0]!.replace(/^0+(?=\d)/, ''); // strip leading zeros but keep single 0
  const decPart = parts[1]; // may be undefined

  // Format integer part with Indian commas
  let formatted = '';
  if (intPart.length <= 3) {
    formatted = intPart || '0';
  } else {
    const lastThree = intPart.slice(-3);
    const remaining = intPart.slice(0, -3);
    const pairs: string[] = [];
    for (let i = remaining.length; i > 0; i -= 2) {
      pairs.unshift(remaining.slice(Math.max(0, i - 2), i));
    }
    formatted = pairs.join(',') + ',' + lastThree;
  }

  let result = (isNegative ? '-' : '') + formatted;
  if (hasTrailingDot) {
    result += '.';
  } else if (decPart !== undefined) {
    result += '.' + decPart;
  }

  return result;
}

/**
 * Format date as DD/MM/YYYY
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!(d instanceof Date) || isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format phone as +91 XXXXX XXXXX
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  if (cleaned.length === 12 && cleaned.startsWith('91'))
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  return phone;
}

/**
 * Normalize a phone to E.164 for tel:/wa.me/ links so mobile OS dialers can parse it.
 * Accepts: "917419600308" → "+917419600308", "7419600308" → "+917419600308",
 *          "+91 74196 00308" → "+917419600308", "0917419600308" → "+917419600308".
 * Defaults to India (+91) if no country code detected.
 */
export function toE164(phone: string | null | undefined, defaultCountry: string = '91'): string {
  if (!phone) return '';
  let cleaned = String(phone).replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) {
    // Already has +, just strip non-digits after the +
    return '+' + cleaned.slice(1).replace(/\D/g, '');
  }
  // Strip leading 0 (trunk prefix) or 00 (international dialling prefix)
  cleaned = cleaned.replace(/^00/, '').replace(/^0+/, '');
  // 10-digit local → prepend default country code
  if (cleaned.length === 10) return `+${defaultCountry}${cleaned}`;
  // 12-digit starting with 91 (India) → prepend +
  if (cleaned.length === 12 && cleaned.startsWith('91')) return `+${cleaned}`;
  // 11-15 digit with any country code → prepend +
  if (cleaned.length >= 11 && cleaned.length <= 15) return `+${cleaned}`;
  // Fallback — return as-is with + if it's at least 7 digits
  if (cleaned.length >= 7) return `+${cleaned}`;
  return phone;
}

/**
 * Build a tel: href that mobile dialers will correctly parse.
 */
export function telHref(phone: string | null | undefined): string {
  const e164 = toE164(phone);
  if (!e164) return '';
  // Use literal '+' per RFC 3966. Don't URL-encode as %2B — most Android
  // dialers don't decode it and end up dialing "91XXXXXXXXXX" instead of
  // "+91XXXXXXXXXX".
  return `tel:${e164}`;
}

/**
 * Imperatively dial a phone number. Use this in an onClick handler instead of
 * `href={telHref(phone)}` for maximum compatibility: some Android browsers /
 * WebViews mangle tel: hrefs, but always honour window.location assignment.
 */
export function dialPhone(phone: string | null | undefined): void {
  const href = telHref(phone);
  if (!href) return;
  // Use an invisible anchor + click() because it is the most reliable cross-
  // browser way to trigger the system tel: handler without navigation.
  const a = document.createElement('a');
  a.href = href;
  a.rel = 'noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Build a wa.me link (WhatsApp) — wa.me requires country code without the +.
 */
export function waHref(phone: string | null | undefined, message?: string): string {
  const e164 = toE164(phone);
  if (!e164) return '';
  const digits = e164.replace(/\D/g, '');
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/**
 * Get Indian financial year
 */
export function getFinancialYear(date: Date = new Date()): string {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  if (month >= 4) return `FY ${year}-${String(year + 1).slice(-2)}`;
  return `FY ${year - 1}-${String(year).slice(-2)}`;
}
